import React from "react";
import { OverviewHealth } from "../analyticsApi";
import { bucketLabel, formatDecimal, healthBand, longDate } from "../analyticsUtils";
import { CARD_PAD, Caption } from "./ui";

const BAND = {
  green: { text: "text-emerald-600 dark:text-emerald-400", ring: "ring-emerald-500/30", stroke: "#1baf7a", label: "Healthy" },
  amber: { text: "text-amber-600 dark:text-amber-400", ring: "ring-amber-500/30", stroke: "#eda100", label: "Needs attention" },
  red: { text: "text-rose-600 dark:text-rose-400", ring: "ring-rose-500/30", stroke: "#e34948", label: "At risk" },
};

function ScoreLine({ series, stroke }: { series: OverviewHealth["series"]; stroke: string }) {
  const w = 240;
  const h = 56;
  const step = w / Math.max(1, series.length - 1);
  const pts = series
    .map((p, i) => (typeof p.score === "number" ? { x: i * step, y: h - 4 - (p.score / 100) * (h - 8), label: bucketLabel(p.month), score: p.score } : null))
    .filter((p): p is NonNullable<typeof p> => p != null);
  if (pts.length === 0) {
    return <p className="text-xs text-slate-400 dark:text-slate-500">No monthly history yet</p>;
  }
  return (
    <svg width="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="h-14" role="img" aria-label="Health score, last 12 months">
      <line x1={0} x2={w} y1={h - 4 - 0.8 * (h - 8)} y2={h - 4 - 0.8 * (h - 8)} stroke="currentColor" strokeOpacity={0.15} strokeDasharray="3 3" />
      <line x1={0} x2={w} y1={h - 4 - 0.6 * (h - 8)} y2={h - 4 - 0.6 * (h - 8)} stroke="currentColor" strokeOpacity={0.15} strokeDasharray="3 3" />
      {pts.length > 1 && (
        <polyline points={pts.map((p) => `${p.x},${p.y}`).join(" ")} fill="none" stroke={stroke} strokeWidth={2} strokeLinejoin="round" />
      )}
      {pts.map((p) => (
        <circle key={p.label} cx={p.x} cy={p.y} r={3} fill={stroke}>
          <title>{`${p.label} · ${p.score}`}</title>
        </circle>
      ))}
    </svg>
  );
}

export default function HealthScoreCard({ health }: { health: OverviewHealth }) {
  const band = healthBand(health.score);
  const style = band ? BAND[band] : null;
  const change = health.change;
  return (
    <div className={`${CARD_PAD} ring-1 ${style?.ring ?? "ring-slate-200/60"}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Clinic Health Score</p>
          <div className="flex items-baseline gap-2 mt-1">
            <span className={`text-5xl font-bold tabular-nums ${style?.text ?? "text-slate-800 dark:text-slate-100"}`}>
              {health.score == null ? "—" : formatDecimal(health.score)}
            </span>
            <span className="text-sm text-slate-400 dark:text-slate-500">/ 100</span>
          </div>
          {style && <p className={`text-xs font-medium mt-1 ${style.text}`}>{style.label}</p>}
        </div>
        <div className="text-right">
          {typeof change === "number" ? (
            <p className={`text-sm font-medium ${change > 0 ? "text-emerald-600 dark:text-emerald-400" : change < 0 ? "text-rose-600 dark:text-rose-400" : "text-slate-500"}`}>
              {change > 0 ? "▲" : change < 0 ? "▼" : "•"} {formatDecimal(Math.abs(change))} vs last month
            </p>
          ) : (
            <p className="text-sm text-slate-400 dark:text-slate-500">No previous month</p>
          )}
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
            Based on {health.basedOn} of {health.total} areas
          </p>
        </div>
      </div>
      <div className="mt-4 text-slate-500 dark:text-slate-400">
        <ScoreLine series={health.series} stroke={style?.stroke ?? "#64748b"} />
        <div className="flex justify-between text-[10px] text-slate-400 dark:text-slate-500 mt-1">
          <span>{health.series[0] ? bucketLabel(health.series[0].month) : ""}</span>
          <span>{health.series.length ? bucketLabel(health.series[health.series.length - 1].month) : ""}</span>
        </div>
      </div>
      <Caption>As of {longDate(health.asOfDay)} · 80+ healthy, 60–79 needs attention, below 60 at risk</Caption>
    </div>
  );
}
